<script lang="ts" setup>
import { computed, type PropType } from 'vue';

import useNavigationLinks from '@exchange/layouts/app-header/useNavigationLink';
import { accountService } from '@exchange/libs/account/service/src';
import { useLogInOut } from '@exchange/libs/auth/ui/src';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import ExchangeMobileOverlay from '@exchange/libs/overlays/ExchangeMobileOverlay.vue';

import ProfileUserSection from './ProfileUserSection.vue';

defineProps({
  /** Function to close a menu */
  closeMenu: { type: Function as PropType<() => void>, default: () => {} },
});

const { isXS } = useMQBreakpoints();
const { processingLogOut, logout } = useLogInOut();

const { navLinks } = useNavigationLinks();

const { userName, userEmail, accountUserChecksActionRequired, accountIdHolder, userIsBroker } = accountService;

const contentNotScrollable = computed(() => accountUserChecksActionRequired.value || processingLogOut.value);

const profileLinks = computed(() => {
  const { settings, levels, partners, tradingAccounts, documents, exports, help, blog, legal, keyInformationDocuments } = navLinks.value;

  const links = [settings, levels, partners, documents, ...(userIsBroker.value ? [] : [exports]), help, blog, legal, keyInformationDocuments];

  if (!isXS.value) {
    links.splice(0, 0, tradingAccounts);
  }

  return links;
});
</script>

<template>
  <div
    :class="['profile-menu-content', { 'profile-menu-content--not-scrollable': contentNotScrollable }]"
    testid="account-menu-content"
  >
    <exchange-mobile-overlay
      v-if="isXS"
      class="profile-menu-content__overlay"
    />
    <profile-user-section
      class="profile-menu-content__user-section"
      :with-secondary-info="isXS"
      :user-email="userEmail"
      :account-holder-id="accountIdHolder"
      :user-name="userName"
    />
    <div class="profile-menu-content__navigation-section">
      <div class="profile-menu-content__navigation">
        <x-list-item
          v-for="link in profileLinks"
          :key="link.id"
          class="profile-menu-content__navigation-item"
          :left-icon-inherit-stroke="true"
          :left-icon-name="link.icon"
          :left-icon-warning="link.iconWithWarning"
          :left-icon-info="link.iconWithInfo"
          :title="link.title"
          :router-link="link.route"
          :href="link.url"
          :testid="`account-link-${link.id}`"
          @click="closeMenu"
        >
          <template #label-additional-content>
            <x-button
              v-if="isXS && !link.external"
              class="profile-menu-content__arrow-link"
              size="3xs"
              variant="clear"
            >
              <ot-icon
                class="profile-menu-content__arrow-link-icon"
                name="arrow-right"
                uncolored-stroke="uncolored-stroke"
              />
            </x-button>
          </template>
        </x-list-item>
      </div>
      <div class="profile-menu-content__logout">
        <x-button
          size="xs"
          :block="isXS"
          :variant="isXS ? 'base' : 'dark-outline'"
          @click="logout(isXS)"
        >
          <ot-icon
            class="profile-menu-content__logout-icon"
            name="logout"
          />
          <span testid="account-menu-logout">{{ $t('modules.userMenu.logout') }}</span>
        </x-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.profile-menu-content {
  &__user-section {
    padding-bottom: 16px;
  }

  &__navigation-section {
    display: flex;
    height: 100%;
    flex-direction: column;
  }

  &__navigation {
    .xs & {
      padding-top: 8px;
    }
  }

  &__navigation-item {
    .xs & {
      height: 44px;
    }

    &:hover {
      border-radius: var(--app-border-radius);
      background-color: rgb(var(--v-theme-elevation-2));
    }
  }

  &__arrow-link {
    margin-left: auto;
    color: rgb(var(--v-theme-text-primary));
  }

  &__arrow-link-icon {
    width: 18px;
  }

  &__logout {
    --logout-padding-top: 8px;

    display: flex;
    justify-content: center;
    padding-top: var(--logout-padding-top);

    .xs & {
      --logout-padding-top: 16px;
    }
  }

  &__logout-icon {
    width: 20px;
    margin-right: 4px;
  }
}

.profile-menu-content--not-scrollable {
  position: relative;
  overflow: hidden;
  height: 100%;
}
</style>
